import type { ColumnDef } from "@tanstack/react-table";

import { CheckIcon, XIcon } from "lucide-react";
import { memo, useMemo, useState } from "react";

import { Button } from "@/components/button";
import { ConfirmationModal } from "@/components/confirmation-modal";
import { DraggableTable as Table } from "@/components/table";
import { toast } from "@/components/toast/toast-queue";

import type { PendingApplication } from "./types";

import { useApproveApplication } from "./hooks/useApproveApplication";
import { useRejectApplication } from "./hooks/useRejectApplication";

interface PendingApplicationsTableProps {
  applications: PendingApplication[];
  isLoading?: boolean;
}

const DateCell = memo(function DateCell({ value }: { value: null | string }) {
  if (!value) {
    return (
      <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
        <span className="text-text-tertiary">-</span>
      </div>
    );
  }

  const date = new Date(value);

  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span className="text-sm text-text-tertiary">
        {date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}
      </span>
    </div>
  );
});

const TextCell = memo(function TextCell({ value }: { value: null | string }) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span className="line-clamp-2 text-sm text-text-tertiary">
        {value || "-"}
      </span>
    </div>
  );
});

const BoldCell = memo(function BoldCell({ value }: { value: null | string }) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span className="line-clamp-2 text-sm font-medium text-text-primary">
        {value || "-"}
      </span>
    </div>
  );
});

interface ActionsCellProps {
  application: PendingApplication;
}

function ActionsCell({ application }: ActionsCellProps) {
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const { isPending: isApproving, mutate: approve } = useApproveApplication();
  const { isPending: isRejecting, mutate: reject } = useRejectApplication();

  const handleApprove = () => {
    approve(
      { id: application.id },
      {
        onError: (error) => {
          setShowApproveModal(false);
          toast.add(
            {
              description: error.message || "Failed to approve application",
              title: "Error",
              type: "error",
            },
            { timeout: 5000 },
          );
        },
        onSuccess: () => {
          setShowApproveModal(false);
          toast.add(
            {
              description: `Application for ${application.fullName} has been approved`,
              title: "Application Approved",
              type: "success",
            },
            { timeout: 5000 },
          );
        },
      },
    );
  };

  const handleReject = () => {
    reject(
      { id: application.id },
      {
        onError: (error) => {
          setShowRejectModal(false);
          toast.add(
            {
              description: error.message || "Failed to reject application",
              title: "Error",
              type: "error",
            },
            { timeout: 5000 },
          );
        },
        onSuccess: () => {
          setShowRejectModal(false);
          toast.add(
            {
              description: `Application for ${application.fullName} has been rejected`,
              title: "Application Rejected",
              type: "success",
            },
            { timeout: 5000 },
          );
        },
      },
    );
  };

  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden flex items-center gap-2">
      <Button
        color="color"
        isLoading={isApproving}
        onPress={() => setShowApproveModal(true)}
        size="sm"
        variant="primary"
      >
        <CheckIcon className="size-4" />
        Approve
      </Button>
      <Button
        color="destructive"
        isLoading={isRejecting}
        onPress={() => setShowRejectModal(true)}
        size="sm"
        variant="secondary"
      >
        <XIcon className="size-4" />
        Reject
      </Button>

      <ConfirmationModal
        confirmButtonColor="color"
        confirmButtonText="Approve"
        isOpen={showApproveModal}
        onConfirm={handleApprove}
        onOpenChange={setShowApproveModal}
        text={`Are you sure you want to approve the application for ${application.fullName} (${application.email})? This will create their account and send them an email to set up their password.`}
        title="Approve Application"
      />

      <ConfirmationModal
        confirmButtonColor="destructive"
        confirmButtonText="Reject"
        isOpen={showRejectModal}
        onConfirm={handleReject}
        onOpenChange={setShowRejectModal}
        text={`Are you sure you want to reject the application for ${application.fullName} (${application.email})? This action cannot be undone.`}
        title="Reject Application"
      />
    </div>
  );
}

export const PendingApplicationsTable = memo(function PendingApplicationsTable({
  applications,
  isLoading = false,
}: PendingApplicationsTableProps) {
  const columns: ColumnDef<PendingApplication>[] = useMemo(
    () => [
      {
        accessorKey: "fullName",
        cell: ({ row }) => <BoldCell value={row.original.fullName} />,
        header: "Name",
        size: 180,
      },
      {
        accessorKey: "email",
        cell: ({ row }) => <TextCell value={row.original.email} />,
        header: "Email",
        size: 220,
      },
      {
        accessorKey: "companyName",
        cell: ({ row }) => <TextCell value={row.original.companyName} />,
        header: "Company",
        size: 180,
      },
      {
        accessorKey: "creativeSupportType",
        cell: ({ row }) => {
          const value = row.original.creativeSupportType;
          const other = row.original.creativeSupportOther;

          return <TextCell value={value === "other" ? other : value} />;
        },
        header: "Support Type",
        size: 150,
      },
      {
        accessorKey: "industry",
        cell: ({ row }) => {
          const value = row.original.industry;
          const other = row.original.industryOther;

          return <TextCell value={value === "other" ? other : value} />;
        },
        header: "Industry",
        size: 150,
      },
      {
        accessorKey: "createdAt",
        cell: ({ row }) => <DateCell value={row.original.createdAt} />,
        header: "Applied",
        size: 120,
      },
      {
        accessorKey: "emailVerifiedAt",
        cell: ({ row }) => <DateCell value={row.original.emailVerifiedAt} />,
        header: "Email Verified",
        size: 120,
      },
      {
        cell: ({ row }) => <ActionsCell application={row.original} />,
        header: "Actions",
        id: "actions",
        size: 220,
      },
    ],
    [],
  );

  return (
    <Table
      columns={columns}
      data={applications}
      enableDragAndDrop={false}
      getRowId={(row) => row.id.toString()}
      isLoading={isLoading}
    />
  );
});
